import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/atoms/Button";
import { DiaryCalendar, type DiaryCalendarViewType } from "@/modules/diary/DiaryCalendar";
import { DiaryDetailDrawer } from "@/modules/diary/DiaryDetailDrawer";
import { availabilityService } from "@/services/availabilityService";
import type { DiaryEntrySummary } from "@/types/entities";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { AvailabilityDrawer, type AvailabilityProperty } from "./AvailabilityDrawer";

interface Props {
  property: AvailabilityProperty;
}

/**
 * Property availability calendar. Reuses the diary calendar look & feel; lets the
 * agent draw availability windows (opening {@link AvailabilityDrawer}) and — like the
 * main diary calendar — click an existing event to view/edit it (DiaryDetailDrawer).
 * Shows the property's AVAILABILITY entries (filtered via the diary `entryType` param).
 */
export function AvailabilityCalendar({ property }: Props) {
  const [entries, setEntries] = useState<DiaryEntrySummary[]>([]);
  const [view, setView] = useState<DiaryCalendarViewType>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [seedDate, setSeedDate] = useState<Date | undefined>(undefined);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailOccurrenceDate, setDetailOccurrenceDate] = useState<Date | null>(null);
  // The drawer takes a list (it is shared with the properties list's bulk flow); here it
  // is always the one property, kept stable so the drawer doesn't reload on every render.
  const availabilityProperties = useMemo(() => [property], [property]);

  const reload = useCallback(() => {
    availabilityService
      .listForProperty(property.id)
      .then((res) => setEntries(res.content ?? []))
      .catch((error) => {
        setEntries([]);
        toast.error(
          getApiErrorMessage(error) ?? "Failed to load availability. Please try again.",
          { toastId: "property-availability-load-error" },
        );
      });
  }, [property.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openForDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return;
    setSeedDate(date);
    setDrawerOpen(true);
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-bridge-fg">Availability</h3>
        <Button variant="primary" size="sm" onClick={() => openForDate(new Date())}>
          <Plus size={16} className="mr-1" /> Add availability
        </Button>
      </div>

      {/* Tall, viewport-relative height so the calendar fills the drawer/tab and is fully
          visible (a fixed small height collapsed it). Same chrome as the main diary calendar. */}
      <div className="h-[78vh] min-h-[600px] glass rounded-lg overflow-hidden border border-bridge-border/60">
        <DiaryCalendar
          entries={entries}
          view={view}
          onViewChange={setView}
          currentDate={currentDate}
          onCurrentDateChange={setCurrentDate}
          onEntryClick={(entry, occurrenceDate) => {
            setDetailId(entry.id);
            setDetailOccurrenceDate(occurrenceDate);
          }}
          onDateClick={openForDate}
        />
      </div>

      <AvailabilityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        properties={availabilityProperties}
        seedDate={seedDate}
        onCreated={reload}
      />

      <DiaryDetailDrawer
        open={detailId != null}
        entryId={detailId}
        crmSyncStatus={entries.find((e) => e.id === detailId)?.crmSyncStatus || entries.find((e) => e.id === detailId)?.syncAlto}
        occurrenceDate={detailOccurrenceDate}
        onClose={() => { setDetailId(null); setDetailOccurrenceDate(null); }}
        onSaved={() => reload()}
      />
    </div>
  );
}
